import { Fetch, initFetch } from '@trp-ta-nitro/http-js';
import * as Domains from '../domains';
import * as Env from '../env';
import * as Location from '../location';
import { ConfigUrl, RequestInterceptor } from '../types';
import * as Url from '../url';
import RequestOptions from '../http/RequestOptions';

export interface LogoutCallback {
  app: string;
  type: string;
}

export interface FetchLogoutCallback extends LogoutCallback {
  apiKey?: string;
  type: 'fetch';
  url: string;
}

export interface CommonLogoutHooks {
  // is called before a fetch request is made, allowing the request options to
  // be augmented...widely used in automated tests
  requestInterceptor?: RequestInterceptor;
}

function coerceRegisterUrl(env: string, registerUrl: ConfigUrl | undefined): ConfigUrl {
  if (Url.isUrlString(registerUrl)) {
    return registerUrl;
  }
  const defaultUrl = {
    host: Domains.External.cloudApiDomain(env),
    path: '/common-login-public/service/auth/logout/callbacks',
    scheme: 'https',
  };
  return Object.assign({}, defaultUrl, registerUrl);
}

export interface CommonLogoutCallbackOptions {
  // the apiKey is required because callbacks are registered through apigee
  apiKey: string;
  // the domain to use for all default url configuration
  baseDomain?: string;
  // the execution environment the app is in, will be automatically determined by domain if not provided
  environment?: string;
  // a fetch implementation to use
  fetch?: Fetch<RequestInit, Response>;
  // functions to influence the behavior of the registration process
  hooks?: CommonLogoutHooks;
  // passed in Location, not meant to be overridden, but an explicit dependency to facilitate testing
  location?: Location;
  // the url to use to register the logout callback, the path from this url can be modified by using
  // the registerPathForType hook
  registerUrl?: ConfigUrl;
}

/**
 * Represents the logout callback mechanism provided by common logout. The expected workflow is:
 *
 *   - Instantiate an instance of this class
 *   - Register a callback using register() (or one of the callback-specific methods)
 *   - When any other app uses the common logout page, the callback will be executed
 *
 * This only applies to external (troweprice.com) users.
 */
export default class CommonLogoutCallback {
  private readonly environment: string;
  private readonly fetch: Fetch<RequestInit, Response>;
  private readonly hooks: CommonLogoutHooks;
  private readonly location: Location;
  private readonly registerUrl: ConfigUrl;
  private readonly requestOptions: RequestOptions;

  constructor(options: CommonLogoutCallbackOptions) {
    this.location = Location.checkedLocation(
      options.location ?? Location.windowLocation(),
    );

    this.environment =
      options.environment ?? Env.environmentFromExternalLocation(this.location);
    this.fetch = options.fetch ?? initFetch().build();
    this.hooks = options.hooks ?? {};
    this.registerUrl = coerceRegisterUrl(this.environment, options.registerUrl);
    this.requestOptions = new RequestOptions({
      apiKey: options.apiKey,
    });
  }

  /**
   * A wrapper around fetch that uses the configured fetch option applies an optionally configured
   * request interceptor to augment the request.
   *
   * @param url
   * @param options
   */
  private async fetchWithInterceptor(url: string, options: RequestInit) {
    if (this.hooks?.requestInterceptor) {
      options = this.hooks.requestInterceptor(url, options);
    }
    return this.fetch(url, options);
  }

  /**
   * Registers one or more common logout callbacks for execution when any app redirects to the
   * common logout page.
   *
   * This method forwards the passed payload on to the /register endpoint without validation,
   * so the caller can provide something invalid. The other convenience methods offer safer
   * uses. However, this means that future callback types can be used without updating this
   * library.
   *
   * If register fails a simple error will be thrown. Inspect the registration network hit in
   * the browser's console for more information.
   *
   * @param logoutCallbacks
   */
  async register(...logoutCallbacks: LogoutCallback[]) {
    const url = Location.buildUrl(this.location, this.registerUrl);
    const response = await this.fetchWithInterceptor(
      url,
      this.requestOptions
        .options()
        .withApiKey()
        .withMethod('POST')
        .withJsonBody(logoutCallbacks)
        .build(),
    );
    if (!response.ok) {
      throw new Error(
        `Could not register logout callback: ${response.statusText} (${response.status})`,
      );
    }
  }

  /**
   * Registers a 'fetch' callback. The callback will be executed by way of a browser fetch on
   * logout.
   *
   * @param app The identifier of the specific app the logout is for.
   * @param url The url to use with browser fetch. Will be called with a GET.
   * @param apiKey An optional apiKey to use with the fetch callback when executed. Will be passed
   *               in the apikey header.
   */
  registerFetch(app: string, url: string, apiKey?: string) {
    const callback: FetchLogoutCallback = {
      app,
      type: 'fetch',
      url,
    };
    if (apiKey) {
      callback.apiKey = apiKey;
    }
    return this.register(callback);
  }
}
